import type { ReactNode } from "react";

import { Select } from "@/components/inputs";
import { useModels, useProducts } from "@/modules/products/data";

/** THE scope bar — every Products subpage picks what it is looking at here.
 *
 *  Dattatray: *"Right now the right top side product select is not doing a
 *  great job UX wise"* and *"All of these selections should be in one line so
 *  its easier for user to understand."*
 *
 *  The old picker sat in `PageHead`'s right slot, visually part of the
 *  heading, so it read as decoration rather than as the control that decides
 *  what the whole page shows. Worse, the pages that need a SECOND choice —
 *  which model, which tool — had nowhere to put it, so those choices ended up
 *  inside modals, one step removed from the thing they filter.
 *
 *  So: one row, left to right, in the order a person makes the decisions.
 *  It is a JOURNEY (his word) and the row is the journey made visible —
 *  Product first, then the narrower choice, then the action.
 */
export function ScopeBar({ children, right }: {
  children?: ReactNode;
  /** The action this scope enables — Upload, Add, Publish. Right-aligned so
   *  it reads as the end of the sentence the selectors started. */
  right?: ReactNode;
}) {
  return (
    // ALIGN ON TOPS, NOT BOTTOMS. Dattatray: "Show them but ... dont break
    // the picker alignment with other options."
    //
    // `flex-end` aligned the BOTTOMS, so a field carrying a hint sat one line
    // higher than its neighbours and the row of controls went ragged. Every
    // ScopeField is label-then-control, and every label is one line, so
    // aligning tops puts every control on the same baseline and lets the
    // hints hang below without moving anything.
    <div className="panel panel-pad" style={{
      display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap",
      marginBottom: 12,
    }}>
      {children}
      {right && <ScopeAction push>{right}</ScopeAction>}
    </div>
  );
}

/** A button in the bar, lined up with the CONTROLS rather than the labels.
 *
 *  A bare button has no label above it, so under `flex-start` it would sit a
 *  line higher than every select. This reserves the label's line so the
 *  action lands on the same baseline as the things it acts on.
 */
export function ScopeAction({ children, push = false }: {
  children: ReactNode;
  /** push to the far right — true in the scope bar, false in a form row
   *  where the action follows the last field it depends on */
  push?: boolean;
}) {
  return (
    <span style={{ marginLeft: push ? "auto" : undefined,
                   display: "flex", flexDirection: "column", gap: 4 }}>
      <span className="cap" aria-hidden style={{ visibility: "hidden" }}>.</span>
      <span>{children}</span>
    </span>
  );
}

/** One labelled control inside the bar. Labels sit ABOVE rather than beside,
 *  so the controls line up on their baselines however long the labels are. */
export function ScopeField({ label, hint, children }: {
  label: string; hint?: string; children: ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span className="cap">{label}</span>
      {children}
      {/* Hangs BELOW the control. With the bar aligned on tops this cannot
          shift anything beside it, which is what let the hints stay. */}
      {hint && (
        <span className="cap" style={{ opacity: 0.7, maxWidth: 240, lineHeight: 1.3 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

/** Step 1 of every journey: which product. NOTHING IS PRE-SELECTED.
 *
 *  Dattatray: *"dont select any dropdown option by default, let the user
 *  select."*
 *
 *  This reverses a fix from an hour earlier, and the reversal is the right
 *  call. Display order put `vehicle` first, vehicle is empty, so every page
 *  opened on nothing and read as broken — I "fixed" it by defaulting to the
 *  first product that HAD content. That swapped one wrong answer for
 *  another: the page now silently decided what you were looking at, and
 *  which product that was depended on where the data happened to be.
 *
 *  An empty selector asks a question. A pre-filled one answers a question
 *  nobody asked, and the person cannot tell whether they chose it.
 *
 *  So the page shows `<ChoosePrompt>` until a real choice is made. That is
 *  also why this component no longer writes to its own parent's state on
 *  render — the `queueMicrotask` that used to push a default up was a side
 *  effect during render, and it is gone with the behaviour that needed it.
 */
export function ProductSelect({ value, onChange, onlyWithSoftware = false }: {
  value: string; onChange: (kind: string) => void;
  /** Show only products that HAVE SOFTWARE (D-95).
   *
   *  Dattatray: *"even the UDS tool updates, Reference file update, should
   *  be only for SW enabled products. I think permission map can be generic
   *  and its concept also extended to thing other than Software."*
   *
   *  So Reference files and Tool updates pass this; Spaces and the
   *  Permission map do not. A permission vocabulary is about what a person
   *  may DO in a space — inspect, ship, scrap — and none of that needs the
   *  product to run code. */
  onlyWithSoftware?: boolean;
}) {
  const q = useProducts();
  const all = q.data?.products ?? [];
  const rows = onlyWithSoftware ? all.filter((p) => p.has_software) : all;

  // GROUPED, because Dattatray reversed himself and was right both times:
  // *"I think product categories were useful! They make dropdown of product
  // much easier to look at!"*
  //
  // The distinction is what the category is FOR. As a table column it was
  // noise — every row repeating "Control & display" tells you nothing you
  // cannot see. As `<optgroup>` it is the opposite: thirteen flat options is
  // a list you read, five labelled groups is a list you scan. Same data,
  // and the table still does not carry it.
  const groups: [string, typeof rows][] = [];
  for (const p of rows) {
    const last = groups[groups.length - 1];
    if (last && last[0] === p.group) last[1].push(p);
    else groups.push([p.group, [p]]);
  }

  return (
    <ScopeField label="Product">
      <Select value={value} disabled={q.isLoading || !rows.length}
        onChange={(e) => onChange(e.target.value)} style={{ minWidth: 220 }}>
        <option value="">
          {q.isLoading ? "Loading…" : "Select a product…"}
        </option>
        {groups.map(([group, items]) => (
          <optgroup key={group} label={group}>
            {items.map((p) => (
              <option key={p.kind} value={p.kind}>{p.name}</option>
            ))}
          </optgroup>
        ))}
      </Select>
    </ScopeField>
  );
}

/** What a page shows before a product is chosen.
 *
 *  Not an empty table: an empty table means "this product has none", and
 *  "you have not picked one yet" is a different sentence. Rendering the same
 *  thing for both is how a person concludes the feature is broken.
 */
export function ChoosePrompt({ what }: { what: string }) {
  return (
    <div className="panel panel-pad cap">
      Choose a product above to see its {what}.
    </div>
  );
}

/** Step 2 for files and tools: the whole product, or one model.
 *
 *  "" means COMMON — every model of this product. That is not a null choice
 *  and the option says so in words, because "All models" and "no model
 *  selected" look identical in a dropdown and mean opposite things to an
 *  upload.
 *
 *  A product can genuinely have no models — `cluster` carries 17 reference
 *  files and no catalogue model, because it came from Fabric's cluster_meter
 *  while Orbit's hardware is `cluster_c`. The list is simply the one option
 *  then. An earlier version added a hint saying so; Dattatray: "not
 *  required" — the dropdown already shows what there is to pick.
 */
export function ModelSelect({ kind, value, onChange, label = "Applies to" }: {
  kind: string; value: string; onChange: (modelId: string) => void; label?: string;
}) {
  const q = useModels(kind);
  const models = q.data?.models ?? [];

  return (
    <ScopeField label={label}>
      <Select value={value} onChange={(e) => onChange(e.target.value)}
        disabled={!kind || q.isLoading} style={{ minWidth: 240 }}>
        <option value="">Common — every model of this product</option>
        {models.map((m) => (
          <option key={m.model_id} value={m.model_id}>
            {m.name} ({m.versions} version{m.versions === 1 ? "" : "s"})
          </option>
        ))}
      </Select>
    </ScopeField>
  );
}
